import { useCallback, useMemo } from 'react'

import { isOfType } from '~publish/helpers/typeGuards'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'

import { mapDraftContentToPrefillMedia } from './mapDraftContentToPrefillMedia'
import type { ContentItemDraft } from './usePaginatedContentItems'

const EDIT_CTA = 'publish-allChannels-draftsTab-editContentItemDraft-1'
const OPEN_NOTES_CTA =
  'publish-allChannels-draftsTab-openContentItemDraftNotes-1'
const DUPLICATE_CTA =
  'publish-allChannels-draftsTab-duplicateContentItemDraft-1'

export type ContentItemDraftActions = {
  /**
   * Open this draft in the composer's channel-less omnibox and save changes
   * back to the same content item (updateContentItemDraft).
   */
  edit: () => void
  /**
   * Edit, with the composer's Notes panel open: a channel-less draft has no
   * details view of its own, so the composer is its full Notes view.
   */
  openNotes: () => void
  /**
   * Open a fresh composer seeded with this draft's content. Nothing is written
   * to the API — Save creates a new draft, leaving this one untouched.
   */
  duplicate: () => void
}

/**
 * Actions for a channel-less draft card. Both seed the composer with the
 * draft's content and open it with no channel selected (the omnibox); they
 * differ in what Save does. Edit passes the content item id so the composer's
 * channel-less Save updates this draft in place; Duplicate omits it so Save
 * creates a new draft.
 *
 * Media rides the composer's prefill pipeline (see
 * mapDraftContentToPrefillMedia): images, video and document all hydrate,
 * though images are ordered first to satisfy the legacy prefill format.
 * Deletion lives in {@link useDeleteContentItem}, wired through the card's
 * DeleteContentItemMenuItem so it can own its own confirmation dialog.
 */
export function useContentItemDraftActions(
  contentItem: ContentItemDraft,
): ContentItemDraftActions {
  const { editContentItemDraftInComposer, createNewPostInComposer } =
    usePostComposer()

  const draftBody = isOfType(contentItem.body, 'DraftContent')
    ? contentItem.body
    : null

  const prefillPostData = useMemo((): PrefillPostData => {
    if (!draftBody) return {}
    return {
      text: draftBody.text,
      // Seed the draft's tags so Edit shows/keeps them (and Save updates the
      // parent's tags instead of clearing them) and Duplicate copies them.
      tags: contentItem.tags.map((tag) => tag.id),
      ...mapDraftContentToPrefillMedia(draftBody.assets),
    }
  }, [draftBody, contentItem.tags])

  // Edit seeds the schedule selector from the draft's targetDate (as dueAt, unix
  // seconds) so the existing planning date is shown; Duplicate omits it so a
  // copy starts undated.
  const editPrefillPostData = useMemo((): PrefillPostData => {
    if (!contentItem.targetDate) return prefillPostData
    return {
      ...prefillPostData,
      dueAt: Math.floor(new Date(contentItem.targetDate).getTime() / 1000),
    }
  }, [prefillPostData, contentItem.targetDate])

  const edit = useCallback(() => {
    void editContentItemDraftInComposer({
      cta: EDIT_CTA,
      contentItemId: contentItem.id,
      contentItemTitle: contentItem.title,
      prefillPostData: editPrefillPostData,
    })
  }, [
    editContentItemDraftInComposer,
    contentItem.id,
    contentItem.title,
    editPrefillPostData,
  ])

  const openNotes = useCallback(() => {
    void editContentItemDraftInComposer(
      {
        cta: OPEN_NOTES_CTA,
        contentItemId: contentItem.id,
        contentItemTitle: contentItem.title,
        prefillPostData: editPrefillPostData,
      },
      { activeSidePanel: 'notes' },
    )
  }, [
    editContentItemDraftInComposer,
    contentItem.id,
    contentItem.title,
    editPrefillPostData,
  ])

  const duplicate = useCallback(() => {
    void createNewPostInComposer({
      cta: DUPLICATE_CTA,
      channels: [],
      prefillPostData,
    })
  }, [createNewPostInComposer, prefillPostData])

  return useMemo(
    () => ({ edit, openNotes, duplicate }),
    [edit, openNotes, duplicate],
  )
}
